<template>
  <LoadingScreen
    v-show="status === 'pending' || status === 'success'"
    class="bg-surface-default-mid fixed inset-0 top-52 z-20 w-full md:top-60"
    :loading-text="i18n(resolutionFlowTranslations.initialLoadingText)"
  />
  <ContactScreen
    :machine-send="resolutionFlowStateMachine.send"
    :machine-state="resolutionFlowStateMachine.state.value"
    @submit="initiateResolution"
  />
</template>

<script setup lang="ts">
import {
  navigateTo,
  useAsyncData,
  useRoute,
  useRouteLocationWithLocale,
} from '#imports'
import { inject } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { RESOLUTION_OPTION_TYPE_ENUM } from '@backmarket/http-api/src/api-specs-resolution-engine/types/common'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import type { ResolutionOption } from '~/api/resolution-engine/types/GetResolutionOptions.types'
import LoadingScreen from '~/scopes/customer-care/common/components/LoadingScreen/LoadingScreen.vue'
import resolutionFlowTranslations from '~/scopes/customer-care/resolution/ResolutionFlow.translations'
import ContactScreen from '~/scopes/customer-care/resolution/components/ResolutionFlowScreens/screens/Return/ContactScreen/ContactScreen.vue'
import { useInitiateResolutionApi } from '~/scopes/customer-care/resolution/composables/useInitiateResolutionApi'
import { useUploadAttachments } from '~/scopes/customer-care/resolution/composables/useUploadAttachments'
import type { ProvideResolutionFlowMachine } from '~/scopes/customer-care/resolution/machine/ResolutionFlow.machine.types'
import { ResolutionFlowStateMachineKey } from '~/scopes/customer-care/resolution/symbols'
import { CUSTOMER_CARE } from '~/scopes/customer-care/route-names'

const resolutionFlowStateMachine = inject(
  ResolutionFlowStateMachineKey,
) as ProvideResolutionFlowMachine

const { params } = useRoute()
const i18n = useI18n()
const { uploadAndGetAttachmentsIds } = useUploadAttachments()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const initiateXrResolutionKey = getAsyncDataKey(
  'express-replacement-contact-initiate-resolution',
  {
    resolutionId: params.orderlineId,
  },
)
const { data, error, execute, status } = await useAsyncData(
  initiateXrResolutionKey,
  async () => {
    const attachmentHandles = await uploadAndGetAttachmentsIds({
      attachments:
        resolutionFlowStateMachine.state.value.context.customerInput.pictures,
      customerId: resolutionFlowStateMachine.state.value.context.clientId,
    })

    return useInitiateResolutionApi({
      declaredProblem: {
        declaredWarrantyState:
          resolutionFlowStateMachine.state.value.context.customerInput
            .declaredWarrantyState,
        orderlineId: Number(params.orderlineId),
        customerIssues:
          resolutionFlowStateMachine.state.value.context
            .diagnosisV2CustomerIssues,
      },
      resolutionOption: (
        resolutionFlowStateMachine.state.value.context
          .resolutionOptions as ResolutionOption[]
      )[0],
      resolutionRequest: {
        attachmentHandles,
        isDataRemovalConfirmed:
          resolutionFlowStateMachine.state.value.context.customerInput
            .areDataRemoved,
        message:
          resolutionFlowStateMachine.state.value.context.customerInput
            .description,
        shipmentId: resolutionFlowStateMachine.state.value.context.shipment?.id,
      },
    })
  },
  { immediate: false },
)

const initiateResolution = async () => {
  await execute()
  if (error.value) {
    resolutionFlowStateMachine.send('ERROR')
  }

  if (data.value?.resolution?.id) {
    await navigateTo(
      resolveLocalizedRoute({
        name:
          data?.value.resolution?.resolutionOption.type ===
          RESOLUTION_OPTION_TYPE_ENUM.expressReplace
            ? CUSTOMER_CARE.RESOLUTION.EXPRESS_REPLACEMENT.PAYMENT
            : CUSTOMER_CARE.RESOLUTION.RESOLUTION_CONTACT_CONFIRMATION,
        params: {
          resolutionId: data.value.resolution.id,
        },
      }) as RouteLocationRaw,
      {
        external:
          data?.value.resolution?.resolutionOption.type ===
          RESOLUTION_OPTION_TYPE_ENUM.expressReplace,
      },
    )
  }
}

resolutionFlowStateMachine?.send('GO_TO_DESCRIPTION')
</script>

<style scoped></style>
